import type { ReportTable, ReportTimeFilter } from '@erp/shared-types';

/**
 * Compute which column indices should be visible based on time filter
 */
export function getVisibleColumnIndices(
  table: ReportTable,
  timeFilter: ReportTimeFilter,
  year?: number,
): number[] {
  const monthPattern =
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(\s+\d{4})?$/;
  const monthColumnIndices: number[] = [];
  const nonMonthColumnIndices: number[] = [];

  table.columnHeaders.forEach((header, index) => {
    if (monthPattern.test(header)) {
      monthColumnIndices.push(index);
    } else {
      nonMonthColumnIndices.push(index);
    }
  });

  // If no month columns, show all columns
  if (monthColumnIndices.length === 0) {
    return Array.from({ length: table.columnHeaders.length }, (_, i) => i);
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  // For past years, treat 'current' and 'ytd' as December (full year)
  // For current year, use the actual current month
  const effectiveMonthIndex =
    year !== undefined && year < currentYear ? 11 : now.getMonth();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Filter month columns based on time filter
  let visibleMonthIndices: number[] = [];
  switch (timeFilter) {
    case 'ytd': {
      // Show Jan through effective month (December for past years, current month for current year)
      visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
        const header = table.columnHeaders[colIndex];
        const monthMatch = header.match(
          /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/,
        );
        if (!monthMatch) return false;
        const monthName = monthMatch[1];
        const monthIdx = monthNames.indexOf(monthName);
        return monthIdx >= 0 && monthIdx <= effectiveMonthIndex;
      });
      break;
    }
    case 'current': {
      // Show only effective month (December for past years, current month for current year)
      const effectiveMonthName = monthNames[effectiveMonthIndex];
      visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
        const header = table.columnHeaders[colIndex];
        return header.startsWith(effectiveMonthName);
      });
      break;
    }
    case 'all':
    default: {
      // Show all month columns
      visibleMonthIndices = monthColumnIndices;
      break;
    }
  }

  // Combine non-month and visible month columns, maintaining order
  const allVisibleIndices: number[] = [];
  table.columnHeaders.forEach((_, index) => {
    if (
      nonMonthColumnIndices.includes(index) ||
      visibleMonthIndices.includes(index)
    ) {
      allVisibleIndices.push(index);
    }
  });

  return allVisibleIndices;
}
